// Extracted SLA & resolve-faster block for the Contact page
"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  Clock,
  ShieldCheck,
  AlertTriangle,
  ListChecks,
  CheckCircle,
} from "lucide-react";
import type { SlaInfo } from "@/components/public/templates/ContactTemplate";

interface ContactSlaBlockProps {
  slaInfo?: SlaInfo;
  resolveChecklist?: string[];
}

export function ContactSlaBlock({ slaInfo, resolveChecklist }: ContactSlaBlockProps) {
  if (!slaInfo && !resolveChecklist) return null;

  const tiers = slaInfo
    ? [
        {
          label: "Standard",
          icon: Clock,
          iconClass: "text-gray-500",
          text: slaInfo.standard,
        },
        {
          label: "Priority",
          icon: ShieldCheck,
          iconClass: "text-[var(--mininav-active-color,#a855f7)]",
          text: slaInfo.priority,
        },
        {
          label: "Emergency",
          icon: AlertTriangle,
          iconClass: "text-orange-500",
          text: slaInfo.emergency,
        },
      ]
    : [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-10">
      {/* Response Expectations */}
      {slaInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="h-full"
        >
          <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 h-full">
            <div className="flex items-center gap-2.5 mb-6">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: "var(--mininav-active-color, #a855f7)" }}
              >
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 font-satoshi leading-tight">
                  Response Expectations &amp; SLAs
                </h3>
                <p className="text-xs text-gray-400 mt-1">Service response by urgency level</p>
              </div>
            </div>

            <div className="space-y-0">
              {tiers.map((tier, idx) => (
                <div
                  key={tier.label}
                  className={`flex items-start gap-3 py-4 ${idx !== tiers.length - 1 ? "border-b border-gray-100" : ""}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                    <tier.icon className={`w-4 h-4 ${tier.iconClass}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 mb-1">{tier.label}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{tier.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Resolve faster checklist */}
      {resolveChecklist && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="h-full"
        >
          <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 h-full">
            <div className="flex items-center gap-2.5 mb-6">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: "var(--mininav-active-color, #a855f7)" }}
              >
                <ListChecks className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 font-satoshi leading-tight">
                  What to include so we can resolve faster
                </h3>
                <p className="text-xs text-gray-400 mt-1">Share these details to speed resolution</p>
              </div>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {resolveChecklist.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-gray-600 rounded-lg border border-gray-100 bg-gray-50/40 p-3"
                >
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            {/* Emergency callout */}
            <div className="rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-orange-200 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1.5">
                    Emergency &amp; after-hours support
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    If a property is at risk of missing check-in, select
                    &quot;Emergency / Guest-impacting incident&quot; in the form.
                    Include: unit ID, check-in time, access details, and what
                    has already been tried.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default ContactSlaBlock;
